"use client";

import React from "react";
import {Locale} from "~/i18n-config";
import {SearchParamsBoundary} from "./SearchParamsBoundary";
import {LangLink} from "~/components/locales/LangLink";
import clsx from "clsx";
import Link from "next/link";
import {NavigationItem} from "@/utils/content/navigation";
import {InlineText} from "@/components/content/common/InlineText";

function NavigationLinkContent(
    { lang, item, className, prepend, append, active = false, current = false, omittedQueryParams = [], searchParams } : {
    lang: Locale;
    item: NavigationItem;
    className?: string;
    prepend?: React.ReactNode;
    append?: React.ReactNode;
    active?: boolean;
    current?: boolean;
    omittedQueryParams?: readonly string[];
    searchParams: URLSearchParams;
}) {

    const queryParams = new URLSearchParams(searchParams);

    omittedQueryParams.forEach((queryParam) => queryParams.delete(queryParam));

    const hasChildren = item.children && item.children.length > 0;

    const classNames = clsx(
        hasChildren ? "has-children" : "no-children",
        "select-none outline-hidden transition-opacity focus-visible:opacity-75",
        className
    );

    if (item.linkType === "No Link") {
        return (
            <span className={classNames} data-active={active ? "true" : undefined}>
                {prepend}
                <InlineText value={item.name} />
                {append}
            </span>
        );
    }

    const target = item.openInNewTab === "Yes" ? "_blank" : "_self";
    const rel = item.noFollow === "Yes" ? "nofollow" : "follow";

    if (item.linkType === "External" && item.externalLink) {
        return (
            <Link
                href={{
                    pathname: item.externalLink,
                    query: Object.fromEntries(queryParams.entries()),
                }}
                target={target}
                rel={rel}
                data-active={active ? "true" : undefined}
                className={classNames}>
                {prepend}
                <InlineText value={item.name} />
                {append}
            </Link>
        );
    }

    if (item.linkType === "Internal") {
        return (
            <LangLink
                lang={lang}
                href={{
                    pathname: (item.entity && item.entity.value) ? `/${item.entity.value.slug}` : `/${item.pathname}`,
                    query: queryParams,
                }}
                target={target}
                rel={rel}
                aria-current={current ? "page" : undefined}
                data-active={active ? "true" : undefined}
                className={classNames}>
                {prepend}
                <InlineText value={item.name} />
                {append}
            </LangLink>
        );
    }

    return (
        <span className={classNames} data-active={active ? "true" : undefined}>
            {prepend}
            <InlineText value={item.name} />
            {append}
        </span>
    );

}

export function NavigationLink(props: Omit<React.ComponentProps<typeof NavigationLinkContent>, "searchParams">) {
    return <SearchParamsBoundary>{searchParams => <NavigationLinkContent {...props} searchParams={searchParams}/>}</SearchParamsBoundary>;
}
